/**
 * useCreatorLoyalty — v146.42
 *
 * Small shared hook that fetches a creator's loyalty program config
 * from the public engagement endpoint so any storefront / product /
 * cart CTA can render an "Earn N coins" preview without each caller
 * hand-rolling the fetch.
 *
 * Returns:
 *   {
 *     enabled: boolean,
 *     coin_name: string,
 *     earn_rules: { purchase_per_100, coupon_claim, follow, ... },
 *     coinsForAmount(inr) => number,   // convenience
 *   }
 *
 * Cache: per-slug in-memory map. Same visitor viewing multiple
 * products on the same bio only hits the endpoint once.
 *
 * v149.64 — The cache only held finished answers, so every product card
 * mounting at once (six on a bio page) fired its own `engagement` and
 * `my-loyalty-balance` request. One request per slug is now shared by
 * everyone waiting on it. The balance is refetched on `coins:changed`
 * (a redemption), so the cached number never goes stale.
 */
import { useEffect, useState } from "react";
import { fetchEngagement } from "../lib/publicEngagement";

const CACHE = new Map();      // slug -> { enabled, coin_name, earn_rules }
const BAL_CACHE = new Map();  // slug -> { balance, enabled, coin_name }
const INFLIGHT = new Map();   // "prog:slug" | "bal:slug" -> Promise

const backend = () => process.env.REACT_APP_BACKEND_URL;

function once(key, run) {
  if (!INFLIGHT.has(key)) {
    INFLIGHT.set(key, run().finally(() => INFLIGHT.delete(key)));
  }
  return INFLIGHT.get(key);
}

function loadProgram(slug) {
  if (CACHE.has(slug)) return Promise.resolve(CACHE.get(slug));
  return once(`prog:${slug}`, async () => {
    let norm;
    try {
      const d = await fetchEngagement(slug);
      norm = { enabled: !!d.loyalty_enabled, coin_name: d.loyalty_coin_name || "coins",
               earn_rules: d.loyalty_earn_rules || {} };
    } catch {
      norm = { enabled: false, coin_name: "coins", earn_rules: {} };
    }
    CACHE.set(slug, norm);
    return norm;
  });
}

// Signed-in visitor's balance with this creator (silent 0 for anon or if
// the program is disabled). Includes credentials so the session cookie is
// sent through the ingress.
function loadBalance(slug, { fresh = false } = {}) {
  if (!fresh && BAL_CACHE.has(slug)) return Promise.resolve(BAL_CACHE.get(slug));
  return once(`bal:${slug}`, async () => {
    try {
      const rb = await fetch(`${backend()}/api/public/creators/${encodeURIComponent(slug)}/my-loyalty-balance`,
                             { credentials: "include" });
      const d = await rb.json();
      const bnorm = { balance: Number(d.balance || 0), enabled: !!d.enabled, coin_name: d.coin_name || "coins" };
      BAL_CACHE.set(slug, bnorm);
      return bnorm;
    } catch {
      return BAL_CACHE.get(slug) || { balance: 0, enabled: false, coin_name: "coins" };
    }
  });
}

export default function useCreatorLoyalty(creatorSlug) {
  const [state, setState] = useState(() => CACHE.get(creatorSlug) || null);
  const [balance, setBalance] = useState(() => BAL_CACHE.get(creatorSlug)?.balance ?? 0);

  useEffect(() => {
    if (!creatorSlug) return undefined;
    let alive = true;
    loadProgram(creatorSlug).then((p) => { if (alive) setState(p); });
    loadBalance(creatorSlug).then((b) => { if (alive) setBalance(b.balance || 0); });
    const onCoins = () => {
      loadBalance(creatorSlug, { fresh: true }).then((b) => { if (alive) setBalance(b.balance || 0); });
    };
    window.addEventListener("coins:changed", onCoins);
    return () => { alive = false; window.removeEventListener("coins:changed", onCoins); };
  }, [creatorSlug]);

  const coinsForAmount = (inr) => {
    if (!state?.enabled) return 0;
    const per100 = Number(state.earn_rules?.purchase_per_100 || 0);
    if (per100 <= 0) return 0;
    return Math.floor(Math.max(0, Number(inr) || 0) / 100) * per100;
  };

  return {
    enabled: !!state?.enabled,
    coin_name: state?.coin_name || "coins",
    earn_rules: state?.earn_rules || {},
    balance,
    coinsForAmount,
  };
}
